import { useCallback, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Group,
  Loader,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import { useInfiniteQuery } from "@tanstack/react-query";
import { qcReviewService } from "../../services/qcReview.service";
import { QcSlideReviewModal } from "./QcSlideReviewModal";

const QC_REVIEW_QUEUE_PAGE_SIZE = 20;

export function QcSlideReviewQueue({ active }: { active: boolean }) {
  const [caseId, setCaseId] = useState<string>();
  const observerRef = useRef<IntersectionObserver | null>(null);
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetching,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["qc-review-queue"],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => qcReviewService.queue(pageParam),
    getNextPageParam: (lastPage) =>
      lastPage.page * QC_REVIEW_QUEUE_PAGE_SIZE < lastPage.total
        ? lastPage.page + 1
        : undefined,
    enabled: active,
    refetchInterval: 5000,
  });
  const rows = useMemo(
    () => data?.pages.flatMap((page) => page.items) ?? [],
    [data]
  );
  const total = useMemo(() => data?.pages[0]?.total ?? 0, [data]);
  const handleRevealMore = useCallback(() => {
    if (hasNextPage && !isFetching) {
      void fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetching]);
  const attachLoadBoundary = useCallback(
    (node: HTMLDivElement | null) => {
      observerRef.current?.disconnect();
      if (
        !node ||
        !active ||
        !hasNextPage ||
        isFetching ||
        isError ||
        typeof IntersectionObserver === "undefined"
      )
        return;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) return;
          observer.disconnect();
          handleRevealMore();
        },
        { rootMargin: "0px 0px 160px 0px" }
      );
      observerRef.current = observer;
      observer.observe(node);
    },
    [active, handleRevealMore, hasNextPage, isError, isFetching]
  );
  const handleOpen = useCallback(
    (event: React.MouseEvent<HTMLTableRowElement>) =>
      setCaseId(event.currentTarget.dataset.caseId),
    []
  );
  const handleButton = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      setCaseId(event.currentTarget.dataset.caseId);
    },
    []
  );
  const handleClose = useCallback(() => setCaseId(undefined), []);
  return (
    <Stack>
      <Group justify="space-between">
        <Text fw={600}>QC Slide Review ({total})</Text>
        {isFetching && <Loader size="xs" aria-label="Loading QC queue" />}
      </Group>
      {isError && (
        <Alert color="red" title="QC queue unavailable" role="alert">
          QC cases could not be loaded.
        </Alert>
      )}
      <Table highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Case number</Table.Th>
            <Table.Th>Patient</Table.Th>
            <Table.Th>Review</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row) => (
            <Table.Tr
              key={row.id}
              data-case-id={row.id}
              onClick={handleOpen}
              style={{ cursor: "pointer" }}
            >
              <Table.Td>{row.caseNumber}</Table.Td>
              <Table.Td>
                {row.patient?.lastName}, {row.patient?.firstName}
              </Table.Td>
              <Table.Td>
                <Button
                  variant="subtle"
                  data-case-id={row.id}
                  onClick={handleButton}
                >
                  Review slides
                </Button>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      {data && total === 0 && (
        <Text c="dimmed">No QC slides await your review.</Text>
      )}
      {isFetchingNextPage && (
        <Group justify="center" py="xs">
          <Loader size="xs" aria-label="Loading more QC cases" />
        </Group>
      )}
      <Box ref={attachLoadBoundary} h={1} aria-hidden="true" />
      {caseId && <QcSlideReviewModal caseId={caseId} onClose={handleClose} />}
    </Stack>
  );
}
